import PageMeta from "@/components/PageMeta";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { fetchCollection } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteBackTarget, getRouteState } from "@/lib/navigation";

export default function CollectionPage({
  handle,
  title,
  subtitle,
  description,
  ctaLabel,
  backHref,
  backLabel,
  productBasePath,
  cardVariant,
  ctaDescription,
}) {
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    fetchCollection(handle, { first: 50 })
      .then((d) => setCollection(d.collection))
      .catch((err) => console.error("CollectionPage: fetch failed", err))
      .finally(() => setLoading(false));
  }, [handle]);

  const displayTitle = collection?.title || title;
  const displayDesc = collection?.description || description;
  const raw = collection?.products || [];

  // deduplicate by title: keep the version with more images, then higher price
  const deduped = (() => {
    const seen = new Map();
    for (const p of raw) {
      const key = (p.title || "").toLowerCase().trim();
      if (!seen.has(key)) {
        seen.set(key, p);
      } else {
        const ex = seen.get(key);
        const exScore =
          (ex.images?.length || 0) * 10000 +
          parseFloat(ex.priceRange?.minVariantPrice?.amount || 0);
        const newScore =
          (p.images?.length || 0) * 10000 +
          parseFloat(p.priceRange?.minVariantPrice?.amount || 0);
        if (newScore > exScore) seen.set(key, p);
      }
    }
    return [...seen.values()];
  })();

  // images-first sort
  const sorted = [...deduped].sort((a, b) => {
    const aHas = a.images?.length > 0 ? 0 : 1;
    const bHas = b.images?.length > 0 ? 0 : 1;
    return aHas - bHas;
  });

  // for murals, hide products without images entirely
  const isMural = cardVariant === "mural";
  const isCanvas = cardVariant === "canvas";
  const products = isMural
    ? sorted.filter((p) => p.images?.length > 0)
    : sorted;

  const count = products.length;
  const backTarget = getRouteBackTarget(location, "/");
  const backText = location.state?.from ? backLabel : "Back to Home";
  const GRID_CLASSES = { mural: "grid grid-cols-1 sm:grid-cols-2 gap-5", canvas: "grid grid-cols-1 lg:grid-cols-2 gap-6" };
  const PAGE_TITLES = { "canvas-prints": "Canvas Prints Collection", "wall-murals": "Wall Murals Collection" };
  const PAGE_DESCS = {
    "canvas-prints": "Browse all canvas print designs. Gallery-quality printing on premium stretched canvas with optional solid wood framing.",
    "wall-murals": "Browse wall mural designs made to your exact dimensions. High-quality vinyl or wallpaper print, installed to spec.",
  };

  const gridClass = GRID_CLASSES[cardVariant] || "grid grid-cols-2 md:grid-cols-3 gap-5";
  const pageTitle = PAGE_TITLES[handle] || "Collection";
  const pageDesc = PAGE_DESCS[handle] || "Browse our collection of premium prints and signage.";

  return (
    <div
      className="py-20 md:py-28"
      data-testid={`collection-page-${handle}`}
    >
      <PageMeta title={pageTitle} description={pageDesc} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* header */}
        <SectionFadeIn>
          {backHref && backLabel && (
            <Link
              to={backTarget}
              className="inline-flex items-center gap-1.5 text-[11px] text-brand-text-secondary/50 hover:text-brand-gold transition-colors duration-300 tracking-wide uppercase mb-5"
              data-testid="collection-back-link"
            >
              <ArrowLeft size={12} /> {backText}
            </Link>
          )}
          <p className="text-[11px] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
            {subtitle || "Our Collection"}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-brand-text">
            {displayTitle}
          </h1>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            {displayDesc && (
              <p className="text-[14px] text-brand-text-secondary leading-relaxed max-w-xl">
                {displayDesc}
              </p>
            )}
            {!loading && count > 0 && (
              <span className="text-[12px] text-brand-text-secondary/50 tracking-wide tabular-nums">
                {count} {count === 1 ? "piece" : "pieces"}
              </span>
            )}
          </div>
        </SectionFadeIn>

        {/* grid */}
        {loading ? (
          <div className={`mt-14 ${gridClass}`}>
            {Array.from({ length: isMural ? 4 : 6 }).map((_, i) => (
              <div key={`skeleton-${i}`}>
                <div className={`bg-brand-surface border border-brand-border rounded-sm animate-pulse ${(isMural || isCanvas) ? "aspect-[16/9]" : "aspect-[4/5]"}`} />
                {!isMural && (
                  <>
                    <div className="mt-3 h-4 bg-brand-surface/60 rounded animate-pulse w-3/4" />
                    <div className="mt-2 h-3 bg-brand-surface/40 rounded animate-pulse w-1/2" />
                  </>
                )}
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className={`mt-14 ${gridClass}`}>
            {products.map((product, i) => (
              <SectionFadeIn key={product.id} delay={i * 50}>
                <ProductCard product={product} basePath={productBasePath} variant={cardVariant} />
              </SectionFadeIn>
            ))}
          </div>
        ) : (
          <div className="mt-16 text-center py-24 bg-brand-surface/30 border border-brand-border rounded-sm">
            <p className="text-brand-text-secondary mb-6">
              {collection
                ? "No products found in this collection yet."
                : "Products are loading. Please ensure Shopify is connected."}
            </p>
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 border border-brand-gold text-brand-gold hover:bg-brand-gold/10 transition-colors duration-300 rounded-sm px-6 py-3 text-sm font-medium"
              data-testid="collection-empty-quote-cta"
            >
              Request a Custom Quote <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* bottom CTA */}
        {!loading && products.length > 0 && (
          <SectionFadeIn>
            <div className="mt-16 pt-12 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-5">
              <p className="text-[14px] text-brand-text-secondary">
                {ctaDescription || "Don\u2019t see what you need? We produce custom sizes and your own images."}
              </p>
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10 transition-colors duration-300 rounded-sm px-7 py-3 text-[13px] font-medium tracking-wide shrink-0"
                data-testid="collection-custom-quote-cta"
              >
                {ctaLabel || "Request Custom Quote"} <ArrowRight size={15} />
              </Link>
            </div>
          </SectionFadeIn>
        )}
      </div>
    </div>
  );
}
